/**
 * Number formatting utilities — Roblox-style compact notation.
 *
 * Standard w pet collecting games (Pet Simulator X, Adopt Me, etc.) —
 * gracz widzi `1.5K` zamiast `1,500`, `12M` zamiast `12,000,000`,
 * `4.2T` zamiast `4,200,000,000,000`. Kluczowe dla mobile UI (HUD na
 * 320px nie ma miejsca na pełne liczby) i dla genrze pet collectorów.
 *
 * Dodane w 5.0.A FIXES (po Codex MED 1 — HUD overflow na 4 pigułkach +
 * user feedback o Roblox-style K/M/B/T).
 *
 * **Notacja angielska** (Roblox standard):
 *   K  = 10^3   (thousand / tysiąc)
 *   M  = 10^6   (million / milion)
 *   B  = 10^9   (billion / miliard po polsku — UWAGA na confusion)
 *   T  = 10^12  (trillion / bilion po polsku)
 *   Qa = 10^15  (quadrillion)
 *   Qi = 10^18  (quintillion)
 *   Sx = 10^21  (sextillion)
 *   Sp = 10^24  (septillion)
 *   Oc = 10^27  (octillion)
 *   No = 10^30  (nonillion)
 *   Dc = 10^33  (decillion)
 *
 * Po `Dc` suffix jest generowany jak w grach Roblox: aa, ab, ... zz, aaa...
 * Formatter nie używa `Number`, więc nie traci cyfr niezależnie od salda.
 */

/**
 * Sufiksy w kolejności rosnącej. Index w tablicy = liczba grup 1000.
 *  - index 0 = brak sufiksu (0-999)
 *  - index 1 = K  (10^3)
 *  - index 2 = M  (10^6)
 *  - ...
 */
const NAMED_SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc'] as const;
const MAX_COIN_DIGITS = 131_072;

export type CompactNumberInput = number | bigint | string;

// Dwuliterowe nazwy historyczne (Qa, Qi itd.) kolidowałyby z generowaną
// sekwencją. Pomijamy je w `aa..zz`, aby każdy tekst HUD miał dokładnie
// jedno znaczenie i parser zawsze był odwrotnością formattera.
const RESERVED_GENERATED_SUFFIXES = new Set(
  NAMED_SUFFIXES.slice(1).map((suffix) => suffix.toLowerCase()).filter((suffix) => suffix.length >= 2),
);
const RESERVED_TWO_LETTER_INDICES = [...RESERVED_GENERATED_SUFFIXES]
  .map((suffix) => (suffix.charCodeAt(0) - 97) * 26 + (suffix.charCodeAt(1) - 97))
  .sort((a, b) => a - b);
const TWO_LETTER_GENERATED_COUNT = 26 ** 2 - RESERVED_TWO_LETTER_INDICES.length;

function encodeLetters(index: number, length: number): string {
  const chars = Array<string>(length).fill('a');
  for (let i = length - 1; i >= 0; i -= 1) {
    chars[i] = String.fromCharCode(97 + (index % 26));
    index = Math.floor(index / 26);
  }
  return chars.join('');
}

function generatedSuffix(index: number): string {
  if (index < TWO_LETTER_GENERATED_COUNT) {
    let rawIndex = index;
    for (const reservedIndex of RESERVED_TWO_LETTER_INDICES) {
      if (rawIndex < reservedIndex) break;
      rawIndex += 1;
    }
    return encodeLetters(rawIndex, 2);
  }

  let remaining = index - TWO_LETTER_GENERATED_COUNT;
  let length = 3;
  let block = 26 ** length;
  while (remaining >= block) {
    remaining -= block;
    length += 1;
    block = 26 ** length;
  }
  return encodeLetters(remaining, length);
}

function suffixForGroup(group: number): string {
  return group < NAMED_SUFFIXES.length
    ? NAMED_SUFFIXES[group]!
    : generatedSuffix(group - NAMED_SUFFIXES.length);
}

function integerText(value: CompactNumberInput): { sign: string; digits: string } | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    const digits = Math.trunc(abs).toLocaleString('en-US', {
      useGrouping: false,
      maximumFractionDigits: 0,
    });
    return { sign, digits };
  }
  const raw = typeof value === 'bigint' ? value.toString() : value.trim();
  const match = /^(-?)(\d+)$/.exec(raw);
  if (!match) return null;
  const digits = match[2]!.replace(/^0+(?=\d)/, '');
  return { sign: match[1]!, digits };
}

/**
 * Formatuje liczbę w stylu Roblox compact:
 *   formatNumberCompact(0)           → "0"
 *   formatNumberCompact(50)          → "50"
 *   formatNumberCompact(999)         → "999"
 *   formatNumberCompact(1_000)       → "1K"
 *   formatNumberCompact(1_500)       → "1.5K"
 *   formatNumberCompact(12_345)      → "12.3K"
 *   formatNumberCompact(999_999)     → "999K"   (truncate przy 100+ = 0 decimal; NIE „1M" — żeby nie kłamać)
 *   formatNumberCompact(1_000_000)   → "1M"
 *   formatNumberCompact(1_234_567)   → "1.23M"
 *   formatNumberCompact(12_345_678)  → "12.3M"
 *   formatNumberCompact(999_500_000) → "999M"   (3 cyfry, brak miejsca na decimal)
 *   formatNumberCompact(1_000_000_000) → "1B"
 *
 * Reguły precyzji:
 *   - 1-9.999  → 2 decimal (np. "1.23M")
 *   - 10-99.99 → 1 decimal (np. "12.3M")
 *   - 100-999  → 0 decimal (np. "123M")
 *   - 0-999 (bez sufiksu) → integer, format z separatorami pl-PL
 *     (np. "1 234" — `toLocaleString('pl-PL')`)
 *
 * Zaokrąglanie: ZAOKRĄGLENIE W DÓŁ (truncate). Gracz nie powinien widzieć
 * "2K" gdy ma 1999 monet — uczciwie "1.99K". Roblox robi tak samo.
 *
 * Negative numbers: format jak positive z prefix "-".
 * Nieskończoność/NaN: "∞" / "?".
 *
 * @param n liczba do sformatowania
 * @returns string typu "1.5K", "12M", "4.2B"
 */
export function formatNumberCompact(n: CompactNumberInput): string {
  if (typeof n === 'number' && !Number.isFinite(n)) {
    if (Number.isNaN(n)) return '?';
    return n > 0 ? '∞' : '-∞';
  }
  if (typeof n === 'number' && Math.abs(n) < 1_000 && !Number.isInteger(n)) {
    return n.toFixed(1);
  }
  const parsed = integerText(n);
  if (!parsed) return '?';
  const { sign, digits } = parsed;
  if (digits.length <= 3) return sign + digits;

  const group = Math.floor((digits.length - 1) / 3);
  const wholeLength = digits.length - group * 3;
  const decimals = wholeLength === 1 ? 2 : wholeLength === 2 ? 1 : 0;
  const fraction = digits.slice(wholeLength, wholeLength + decimals).replace(/0+$/, '');
  const compact = digits.slice(0, wholeLength) + (fraction ? `.${fraction}` : '');
  return sign + compact + suffixForGroup(group);
}

/**
 * Odwrotność `formatNumberCompact` — czyta to, co gra POKAZUJE.
 *
 *   parseCompactNumber("1000")   → "1000"
 *   parseCompactNumber("1b")     → "1000000000"
 *   parseCompactNumber("1.5T")   → "1500000000000"
 *   parseCompactNumber("1,5t")   → "1500000000000" (polski przecinek)
 *   parseCompactNumber("2 qa")   → "2000000000000000"
 *   parseCompactNumber("")       → null
 *   parseCompactNumber("1X")     → null                (nieznany sufiks)
 *
 * Powstało, bo dziecko wpisujące kwotę lokaty widzi w całej grze „114T", a
 * w pole musiało wklepać piętnaście cyfr (zgłoszenie właściciela 13.09.2026).
 * Sufiksy MUSZĄ być tą samą tablicą, której używa formatter — inaczej gra
 * pokazywałaby coś, czego sama nie przyjmuje.
 *
 * Mnożenie idzie po ŁAŃCUCHU ZNAKÓW, nie przez `* Math.pow(1000, exp)`:
 * `1.234 * 1e15` w liczbach zmiennoprzecinkowych daje 1233999999999999.8,
 * czyli po `floor` kwotę o grosz mniejszą niż wpisana. Przesuwanie przecinka
 * na tekście jest dokładne.
 *
 * Zwraca decimal string, bez przejścia przez JS `number`. `null` oznacza zły
 * format lub przekroczenie limitu PostgreSQL NUMERIC (131 072 cyfr części
 * całkowitej). Ułamek poniżej jedności ucina w dół.
 */
export function parseCompactNumber(text: string): string | null {
  const raw = text.trim().replace(/\s+/g, '').replace(',', '.');
  const match = /^(\d+(?:\.\d+)?)([a-zA-Z]*)$/.exec(raw);
  if (!match) return null;
  const [, liczba, sufiks] = match;
  let exp = NAMED_SUFFIXES.findIndex(
    (s) => s.toLowerCase() === sufiks!.toLowerCase(),
  );
  if (exp < 0) {
    const alpha = sufiks!.toLowerCase();
    if (!/^[a-z]{2,}$/.test(alpha)) return null;
    let offset = alpha.length === 2 ? 0 : TWO_LETTER_GENERATED_COUNT;
    for (let length = 3; length < alpha.length; length += 1) {
      const block = 26 ** length;
      if (!Number.isSafeInteger(block) || offset + block > MAX_COIN_DIGITS) return null;
      offset += block;
    }
    let withinBlock = 0;
    for (const char of alpha) {
      withinBlock = withinBlock * 26 + (char.charCodeAt(0) - 97);
      if (!Number.isSafeInteger(withinBlock) || withinBlock > MAX_COIN_DIGITS) return null;
    }
    if (alpha.length === 2) {
      const skipped = RESERVED_TWO_LETTER_INDICES.filter((index) => index < withinBlock).length;
      withinBlock -= skipped;
    }
    exp = NAMED_SUFFIXES.length + offset + withinBlock;
  }

  const maxGroup = Math.floor((MAX_COIN_DIGITS - 1) / 3);
  if (exp > maxGroup) return null;

  const [calosc, ulamek = ''] = liczba!.split('.');
  const zera = 3 * exp - ulamek.length;
  const cyfry =
    zera >= 0
      ? calosc! + ulamek + '0'.repeat(zera)
      : (calosc! + ulamek).slice(0, calosc!.length + ulamek.length + zera);
  const normalized = (cyfry === '' ? '0' : cyfry).replace(/^0+(?=\d)/, '');
  return normalized.length <= MAX_COIN_DIGITS ? normalized : null;
}

/**
 * Alternatywa: pełna liczba z separatorami pl-PL (`1 234 567`).
 * Używana tam gdzie mamy miejsce na pełną wartość (np. tooltip,
 * leaderboard, modal'e ze szczegółami).
 *
 * Cienka wrap nad `toLocaleString` — istnieje żeby kod miał jeden
 * canonical formatter i było łatwo wprowadzić zmianę globalną.
 */
export function formatNumberFull(n: CompactNumberInput): string {
  if (typeof n === 'number' && !Number.isFinite(n)) {
    if (Number.isNaN(n)) return '?';
    return n > 0 ? '∞' : '-∞';
  }
  if (typeof n === 'number' && !Number.isInteger(n)) return n.toLocaleString('pl-PL');
  const parsed = integerText(n);
  if (!parsed) return '?';
  return parsed.sign + parsed.digits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
}
